import React, { useState, useContext } from 'react';
import PresetDndContext from '../common/DnD/PresetDndContext';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import FieldsetListItem from './FieldsetListItem';
import Icon from '../common/Icon';
import SortableItem from '../common/DnD/SortableItem';
import { handleSortUpdate } from '../common/DnD/sortUpdater';
import Modal from '../common/Modal';
import NewFieldset from './NewFieldset';
import {
  handleCopyFieldset,
  handleDestroyFieldset,
} from './constants/handleFieldset';
import { confirmation, loadingBlock } from '../../helpers/notifications';
import { dragSort } from '../../helpers/sorters';
import { FormContext } from '../../hooks/Contexts';
import MoveFieldModal from './MoveFieldModal';

const FieldsetIndex = ({ formIsChild, fieldsetDeleted, setFieldsets }) => {
  const { form, fieldsets } = useContext(FormContext);
  const canGenerateAutoEmailCaptureFieldset =
    formIsChild &&
    !fieldsets.find((fs) => fs?.name === 'Parent Email Fieldset [Auto]');
  const [creating, setCreating] = useState(false);
  const [viewMoverModal, setViewMoverModal] = useState(false);
  const [fieldToMove, setFieldToMove] = useState(null);
  const hasSubmissions = form.submissions_count > 0;

  const handleFieldMoveModal = (e, field) => {
    e.preventDefault();
    setFieldToMove(field);
    setViewMoverModal(true);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      let sort = fieldsets.map((fs) => fs.id).indexOf(over.id);
      let backup = [...fieldsets];
      setFieldsets((fs) => dragSort(fs, active, over));
      let sorted = await handleSortUpdate('fieldsets', active.id, sort);
      if (!sorted.ok) setFieldsets(backup);
    }
  };

  const handleDestroy = async (e, fieldset) => {
    e.preventDefault();
    await handleDestroyFieldset(fieldset.id, (response) => {
      if (response.ok) fieldsetDeleted(fieldset);
    });
  };

  const handleCopy = async (e, id) => {
    e.preventDefault();
    confirmation.copy(async () => {
      loadingBlock.add('.fieldsets', 'Copying and updating...');
      const response = await handleCopyFieldset(id);
      if (response.ok) {
        setFieldsets((fieldsets) => [...fieldsets, response.data.fieldset]);
      }
      loadingBlock.remove('.fieldsets');
    });
  };

  return (
    <section className='fieldsets'>
      <h2>
        Fieldset Editor
        <Icon
          role='button'
          title='Add fieldset'
          onClick={() => {
            setCreating(true);
          }}
          icon='plus fa-border'
        />
      </h2>

      <Modal
        onClose={() => setCreating(false)}
        title='fieldset-creation-modal'
        show={creating}
      >
        <NewFieldset
          formID={form.id}
          setFieldsets={setFieldsets}
          sortValue={fieldsets.length}
          setCreating={setCreating}
          canGenerateAutoEmailCaptureFieldset={
            canGenerateAutoEmailCaptureFieldset
          }
        />
      </Modal>

      {viewMoverModal && (
        <MoveFieldModal
          fieldsets={fieldsets}
          viewMoverModal={viewMoverModal}
          closeMoverModal={() => setViewMoverModal(false)}
          fieldToMove={fieldToMove}
          setFieldsets={setFieldsets}
        />
      )}

      <div className='editor'>
        <PresetDndContext handleDragEnd={handleDragEnd}>
          <ol className='orderable ui-sortable'>
            {fieldsets.length ? (
              <SortableContext
                strategy={verticalListSortingStrategy}
                items={fieldsets}
              >
                {fieldsets.map((fs, index) => {
                  return (
                    <SortableItem key={fs.id} id={fs.id}>
                      <FieldsetListItem
                        fieldset={fs}
                        hasSubmissions={hasSubmissions}
                        setFieldsets={setFieldsets}
                        handleDestroy={handleDestroy}
                        handleCopy={handleCopy}
                        handleFieldMoveModal={handleFieldMoveModal}
                      />
                    </SortableItem>
                  );
                })}
              </SortableContext>
            ) : (
              <li className='empty'>
                Add some fieldsets to make this form usable...
              </li>
            )}
          </ol>
        </PresetDndContext>
      </div>
    </section>
  );
};

export default FieldsetIndex;
